import { useContext } from 'react';
import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Popover,
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  SelectStateContext,
  SelectValue,
} from 'react-aria-components';
import { cn } from '~/lib/utils';
import { CustomFieldError } from './Common';
import Icon from './Icon';

export interface SelectProps<T = object> extends Omit<
  AriaSelectProps<T>,
  'children' | 'label' | 'description' | 'errorMessage'
> {
  className?: string;
  label?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  placeholder?: string;
  children?: React.ReactNode;
}

function SelectButton({ placeholder }: { placeholder?: string }) {
  const state = useContext(SelectStateContext);

  return (
    <Button className="text-foreground data-focused:border-input-border-focused data-focused:shadow-input-focus data-pressed:border-input-border-focused focus-visible:outline-0 border border-input-border bg-input-background rounded-xl px-4 py-3 flex justify-between items-center cursor-pointer h-12 text-[15px]">
      {state?.selectedKey ? (
        <SelectValue />
      ) : (
        <span className="text-input-placeholder">{placeholder}</span>
      )}
      <Icon name="arrowDropDown" className={cn({ 'rotate-180': state?.isOpen })} />
    </Button>
  );
}

function Select<T = object>({
  className,
  label,
  errorMessage,
  isRequired,
  isReadOnly,
  children,
  placeholder,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect<T>
      {...props}
      className={cn(
        'flex flex-col gap-2 w-full data-disabled:opacity-40',
        { 'pointer-events-none': isReadOnly },
        className,
      )}
      isRequired={isRequired}
      isInvalid={!!errorMessage}
    >
      {label && (
        <Label className="text-sm font-medium text-input-label">
          {label} {isRequired && '*'}
        </Label>
      )}

      <SelectButton placeholder={placeholder} />

      <CustomFieldError errorMessage={errorMessage} />

      <Popover
        className="react-aria-Popover min-w-64 border-1 border-input-border py-2 rounded-xl z-10 bg-card-inner outline-0"
        offset={4}
      >
        <ListBox className="outline-0">{children}</ListBox>
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem(props: ListBoxItemProps) {
  return (
    <ListBoxItem
      {...props}
      className={cn(
        props.className,
        'py-3 px-6 text-[15px] cursor-pointer border-0 hover:bg-input-hover outline-0 data-pressed:bg-input-pressed hover:text-input-pressed-text',
      )}
    />
  );
}

export default Select;
